import { useEffect, useState } from "react";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Swal from "sweetalert2";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import MDBox from "@vendor/components/MDBox";
import PaginationComponent from "@vendor/examples/Pagination";
import { MainLayout } from "@shared/ui/MainLayout";
import { useLoading } from "@shared/hooks/useLoading";

import { JobCard } from "../components/JobCard";
import { SearchInput } from "../components/SearchInput";
import { useVacancies } from "../hooks/useVacancies";
import { useDownloadVacancies } from "../hooks/useDownloadVacancies";
import { SERVICE_UNAVAILABLE_STATUS, VACANCIES_PER_PAGE } from "../constants";

import type { Vacancy } from "../api/vacancyApi";
import type { SearchFormValues } from "../components/SearchInput";

// Referencia estable: evita que el useEffect de paginacion se dispare en cada render
// mientras la query esta en vuelo.
const EMPTY: Vacancy[] = [];

const paginateArray = (array: Vacancy[]): Vacancy[][] =>
  array.reduce<Vacancy[][]>((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / VACANCIES_PER_PAGE);
    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = [];
    }
    resultArray[chunkIndex].push(item);
    return resultArray;
  }, []);

export const VacancyListPage = () => {
  const { t } = useTranslation();
  const { loading, notLoading, thisLoading } = useLoading();
  const { data, isFetching, isError, error } = useVacancies();
  const allVacancy = data ?? EMPTY;
  const { mutate: downloadVacancies, isPending: isDownloading } = useDownloadVacancies();

  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [allVacancies, setAllVacancies] = useState<Vacancy[][]>([]);
  const [page, setPage] = useState(1);
  const [countPage, setCountPage] = useState(1);
  const { control, watch, reset } = useForm<SearchFormValues>({});

  const search = watch("search");

  const createPaginate = (source: Vacancy[]) => {
    const result = paginateArray(source);
    setPage(1);
    setVacancies(result[0] ?? []);
    setAllVacancies(result);
    setCountPage(result.length);
    notLoading();
  };

  useEffect(() => {
    createPaginate(allVacancy);
  }, [allVacancy]);

  useEffect(() => {
    if (isError && error?.response?.status === SERVICE_UNAVAILABLE_STATUS) {
      Swal.fire(
        t("serviceUnavailable"),
        error.response?.data?.message ?? t("internalError"),
        "error"
      );
    }
  }, [isError, error, t]);

  const onSearch = () => {
    thisLoading();
    // `name` es opcional en el contrato y Jackson lo serializa como null cuando falta; el
    // `?? ""` evita que una vacante sin titulo rompa la busqueda entera.
    const filtered = allVacancy.filter((vacancy) =>
      (vacancy.name ?? "").toUpperCase().includes((search ?? "").toUpperCase())
    );
    createPaginate(filtered);
  };

  const onResetSearch = () => {
    thisLoading();
    reset({ search: "" });
    createPaginate(allVacancy);
  };

  const handleChange = (_event: unknown, value: number) => {
    thisLoading();
    setVacancies(allVacancies[value - 1] ?? []);
    setPage(value);
    notLoading();
  };

  const onDownload = () => {
    if (isDownloading) return;
    downloadVacancies(undefined, {
      onSuccess: ({ data: file, headers }) => {
        const link = document.createElement("a");
        link.target = "_blank";
        link.download = t("vacancies") + ".xlsx";
        link.href = window.URL.createObjectURL(
          new Blob([file], { type: headers["content-type"] })
        );
        link.click();
      },
    });
  };

  return (
    <MainLayout loading={loading || isFetching}>
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "start", bottom: "0px" }}>
              <SearchInput
                control={control}
                onSearch={onSearch}
                onResetSearch={onResetSearch}
                t={t}
              />
            </Box>
          </Grid>
          <Grid
            size={{
              md: 2,
              lg: 3,
              xl: 3,
            }}
          />
          <Grid
            size={{
              xs: 12,
              sm: 6,
              md: 4,
              lg: 3,
              xl: 3,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "end", bottom: "0px" }}>
              <Button
                style={{ backgroundColor: "black", color: "white" }}
                onClick={onDownload}
                loading={isDownloading}
                loadingIndicator="Loading..."
                variant="contained"
                fullWidth
              >
                {isDownloading ? t("downloading") : t("exportVacanciesToExcel")}
              </Button>
            </Box>
          </Grid>
          {allVacancies.length > 0 ? (
            <>
              <Grid size={12}>
                {vacancies.map((vacancy, index) => (
                  <JobCard
                    key={`${vacancy.id}${index}`}
                    vacancy={vacancy}
                    thisLoading={thisLoading}
                    notLoading={notLoading}
                  />
                ))}
              </Grid>
              <Grid size={12}>
                <Box sx={{ display: "flex", justifyContent: "center", bottom: "0px" }}>
                  <PaginationComponent page={page} count={countPage} onChangePage={handleChange} />
                </Box>
              </Grid>
            </>
          ) : (
            <Grid size={12}>
              <Box
                sx={{
                  height: "600px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <p>{t("noVacanciesAvailable")}</p>
              </Box>
            </Grid>
          )}
        </Grid>
      </MDBox>
    </MainLayout>
  );
};
